import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL, SITE_NAME, DEFAULT_IMAGE, DEFAULT_DESCRIPTION, getRouteMeta } from '@/seo/config';

const head = () => document.head;

const upsert = (selector, tag, attrs) => {
  let el = head().querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    head().appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const setMeta = (key, value, content) => upsert(`meta[${key}="${value}"]`, 'meta', { [key]: value, content });

const removeMeta = (key, value) => head().querySelector(`meta[${key}="${value}"]`)?.remove();

export const applySEO = ({ title, description, canonical, image, type, noindex, jsonLd, publishedTime }) => {
  document.documentElement.setAttribute('lang', 'fr');
  document.title = title;
  setMeta('name', 'description', description);
  setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
  upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical });
  setMeta('property', 'og:type', type);
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:locale', 'fr_FR');
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', canonical);
  setMeta('property', 'og:image', image);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', image);
  if (publishedTime) setMeta('property', 'article:published_time', publishedTime);
  else removeMeta('property', 'article:published_time');

  head().querySelectorAll('script[data-seo-ld]').forEach((s) => s.remove());
  (jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []).forEach((item) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-ld', 'true');
    script.textContent = JSON.stringify(item);
    head().appendChild(script);
  });
};

// Renders nothing: writes title/meta/canonical/OG/JSON-LD straight into <head>.
export const SEO = ({ title, description = DEFAULT_DESCRIPTION, path, image = DEFAULT_IMAGE, type = 'website', noindex = false, jsonLd, publishedTime }) => {
  const location = useLocation();
  const canonical = `${SITE_URL}${path ?? location.pathname}`;
  const serialized = JSON.stringify({ title, description, canonical, image, type, noindex, jsonLd, publishedTime });

  useEffect(() => {
    applySEO(JSON.parse(serialized));
  }, [serialized]);

  return null;
};

// Route-level defaults (private routes → noindex). Public dynamic pages override with their own <SEO>.
export const RouteSEO = () => {
  const { pathname } = useLocation();
  const meta = getRouteMeta(pathname);
  return <SEO {...meta} path={pathname} />;
};
